import { useEffect, useState } from "react";
import Dialog from "@mui/material/Dialog";
import IconButton from "@mui/material/IconButton";
import Button from "@mui/material/Button";
import Typography from "@mui/material/Typography";
import styled from "styled-components";
import { useAppState } from "../../hooks/useAppState";
import { appState } from "../../../state";
import { notifyStateChange } from "../../../stateBus";
import { apiFetch } from "../../../api";
import { requestLock } from "../../../lockBus";
import { pickAlbum, type PickedAlbum } from "../../../albumPicker";
import { isVideoFilename } from "../../../utils";
import type { MediaItem } from "../../../types";
import type { Track } from "../../hooks/useAudioPlayer";
import { ReelPlayer } from "./ReelPlayer";

// Same raw-Dialog + solid-black-stage pattern as Lightbox.tsx (bypasses
// AppDialog's .glass styling on purpose - this is a full-bleed visual
// experience, not a form-like dialog). position:relative so ReelPlayer's
// root (a position:absolute/inset:0 fill) has an unambiguous positioned
// ancestor. Explicit width:100% is load-bearing, not decorative: as a
// flex container whose "playing" branch renders nothing but
// position:absolute children (which contribute zero to normal-flow
// content sizing), Stage's own intrinsic width - and therefore MUI's
// .MuiDialog-paperFullScreen width:100% class - silently collapsed to 0
// without it (confirmed via a real headless-Chrome DOM inspection, not
// just jsdom, which cannot see this class of layout bug at all).
const Stage = styled.div`
  position: relative;
  background: #000;
  width: 100%;
  height: 100%;
  margin: 0;
  color: #fff;
  display: flex;
  flex-direction: column;
`;

function ReelDialogBody({ onClose }: { onClose: () => void }) {
  const [album, setAlbum] = useState<PickedAlbum | null>(null);
  // null = not loaded yet (or no album chosen); [] = loaded, no photos.
  const [photos, setPhotos] = useState<MediaItem[] | null>(null);
  const [tracks, setTracks] = useState<Track[]>([]);
  const [track, setTrack] = useState<Track | null>(null);
  const [playing, setPlaying] = useState(false);

  // Default selection: whichever album is currently open, if any - the
  // dialog still owns its own picker so it works from a generic entry
  // point too, this is just a starting point.
  useEffect(() => {
    if (appState.currentAlbum === "all") return;
    const current = appState.albums.find((a) => a.id === appState.currentAlbum);
    if (current) setAlbum({ id: current.id, name: current.name });
  }, []);

  useEffect(() => {
    (async () => {
      try {
        const res = await apiFetch("/api/music");
        if (res.status === 401) {
          requestLock("unauthorized");
          return;
        }
        if (!res.ok) return;
        const data = await res.json();
        setTracks(Array.isArray(data.tracks) ? data.tracks : []);
      } catch {
        setTracks([]);
      }
    })();
  }, []);

  // Video files have no thumbnail (server.js's /assets/thumbs 404s for
  // video), so they're filtered out here - photos-only for v1.
  useEffect(() => {
    if (!album) {
      setPhotos(null);
      return;
    }
    let cancelled = false;
    setPhotos(null);
    (async () => {
      try {
        const res = await apiFetch(`/api/albums/${album.id}/media`);
        if (res.status === 401) {
          requestLock("unauthorized");
          return;
        }
        if (!res.ok) throw new Error("Failed to load album media");
        const data = await res.json();
        const items: MediaItem[] = Array.isArray(data.media) ? data.media : [];
        if (!cancelled) setPhotos(items.filter((m) => !isVideoFilename(m.filename)));
      } catch {
        if (!cancelled) setPhotos([]);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [album?.id]);

  async function handleChooseAlbum(): Promise<void> {
    const picked = await pickAlbum("Choose an Album", { includeAll: false });
    if (picked) setAlbum(picked);
  }

  function handleClose(): void {
    setPlaying(false);
    onClose();
  }

  if (playing && album && track && photos && photos.length) {
    return <ReelPlayer photos={photos} track={track} onExit={() => setPlaying(false)} onClose={handleClose} />;
  }

  const noPhotos = photos !== null && photos.length === 0;
  const canStart = !!album && !!track && !!photos && photos.length > 0;

  return (
    <>
      <div className="d-flex align-items-center gap-1 p-3">
        <IconButton aria-label="Back" onClick={handleClose} sx={{ color: "#fff", ml: -1 }}>
          <i className="bi bi-arrow-left" aria-hidden="true" />
        </IconButton>
        <Typography variant="h6" sx={{ color: "#fff", display: "flex", alignItems: "center", gap: 1 }}>
          <i className="bi bi-film text-pink" aria-hidden="true" />
          Memories Reel
        </Typography>
      </div>

      <div className="flex-grow-1 d-flex flex-column align-items-center justify-content-center gap-3 p-4" style={{ overflowY: "auto" }}>
        <Button
          variant="outlined"
          onClick={handleChooseAlbum}
          sx={{ color: "#fff", borderColor: "rgba(255,255,255,0.4)" }}
        >
          {album ? `Album: ${album.name}` : "Choose an Album"}
        </Button>

        {noPhotos && (
          <Typography variant="body2" sx={{ color: "rgba(255,255,255,0.7)", textAlign: "center", maxWidth: 320 }}>
            This album has no photos to include in a reel.
          </Typography>
        )}

        {photos && photos.length > 0 && (
          <Typography variant="body2" sx={{ color: "rgba(255,255,255,0.7)" }}>
            {photos.length} photo{photos.length === 1 ? "" : "s"} ready
          </Typography>
        )}

        <div className="d-flex flex-column gap-2" style={{ width: "100%", maxWidth: 320 }}>
          {!tracks.length && (
            <Typography variant="body2" sx={{ color: "rgba(255,255,255,0.5)" }}>
              No music tracks available.
            </Typography>
          )}
          {tracks.map((t) => (
            <Button
              key={t.filename}
              variant={track?.filename === t.filename ? "contained" : "outlined"}
              color="primary"
              onClick={() => setTrack(t)}
              sx={{ justifyContent: "flex-start", color: track?.filename === t.filename ? undefined : "#fff", borderColor: "rgba(255,255,255,0.4)" }}
            >
              {t.title}
            </Button>
          ))}
        </div>

        <Button variant="contained" color="primary" disabled={!canStart} onClick={() => setPlaying(true)}>
          <i className="bi bi-play-fill me-2" aria-hidden="true" />
          Start Reel
        </Button>
      </div>
    </>
  );
}

// Mounted unconditionally in AppRoot.tsx, self-hiding via appState -
// same convention as Lightbox's currentMediaIndex-driven visibility.
export function ReelDialog() {
  const open = useAppState((s) => s.reelDialogOpen);

  function handleClose(): void {
    appState.reelDialogOpen = false;
    notifyStateChange();
  }

  if (!open) return null;

  return (
    <Dialog open fullScreen onClose={handleClose} container={() => document.getElementById("react-root")} slots={{ paper: Stage }}>
      <ReelDialogBody onClose={handleClose} />
    </Dialog>
  );
}
